'use client';

import { env } from '@/env/client';
import { ReauthenticatableClient } from '@payleo/auth/clients';
import { WebAuth } from '@payleo/auth/web';
import type { AdminCtx } from '@payleo/ewa/admin';
import { manager } from '@payleo/ewa/manage';
import { createWebClient } from '@payleo/ewa/web';
import { ArushaContext as ArushaClient } from '@payleo/platform/arusha';
import { BillingContext } from '@payleo/platform/billing';
import { ImaraClient } from '@payleo/platform/imara';
import { PaymentsContext } from '@payleo/platform/payments';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { EWAController } from 'node_modules/@payleo/ewa/dist/manage/organization';

// Tagging the locally created value
const KeyLocalIdentityAuth = '<identity-local-auth-client>';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const prodfirebaseConfig = {
  apiKey: 'AIzaSyCsJbqoRxaHZJ09UUCKrCBsOfGOPO4b0Z4',
  authDomain: 'auth.payleo.tz',
  projectId: 'payleo-prod',
  appId: '1:511937780516:web:0e9c6fb0f207333bc231b1',
};

const devfirebaseConfig = {
  apiKey: 'AIzaSyCOz4HlLfTbgSOdLf9pI8h-i2zGiThUUtE',
  authDomain: 'payleo-pilot.firebaseapp.com',
  projectId: 'payleo-pilot',
  appId: '1:511937780516:web:0e9c6fb0f207333bc231b1',
};

/**
 * Initialize the payleo identity authentication toolkit client
 * @param config
 * @param env
 * @returns
 */
function createIdentityAuth() {
  const env_: 'local' | 'prod' = env.VITE_APP_ENV === 'production' ? 'prod' : 'local';
  const fbconfig = env_ === 'prod' ? prodfirebaseConfig : devfirebaseConfig;
  const app = getFirebaseApp(fbconfig, env_);
  return WebAuth.create(app, {
    identityV1BaseUrl: env.VITE_IDENTITYKIT_PAYLEO_BASE_URL,
    env: env_,
    firebaseAuthEmulatorHost: env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
  });
}

const identityAuth = createIdentityAuth();

/**
 * [Instantiates and] Provides an instance to interface with the identity platform
 * @returns
 */
export const getIdentityAuth = () => identityAuth;

/**
 * Firebase configuration required by the Identity client
 * to be able to sign the users
 */
type InitIdentityFirebase = {
  apiKey: string;
  appId: string;
  authDomain: string;
};

function getFirebaseApp(config: InitIdentityFirebase, env: 'local' | 'prod') {
  if (env === 'local') {
    console.log('🔥 [local] firebase authentication');
    if (getApps().findIndex((s) => s.name === KeyLocalIdentityAuth) !== -1) {
      return getApp(KeyLocalIdentityAuth);
    }

    return initializeApp(config, {
      name: KeyLocalIdentityAuth,
      automaticDataCollectionEnabled: false,
    });
  }

  // assumed the call path is
  if (getApps().length > 0) {
    return getApp();
  }
  return initializeApp(config, {
    automaticDataCollectionEnabled: false,
  });
}

/**
 * Getting single instance of the `imara` web client
 * @returns
 */
export function getImaraWebClient() {
  const auth = getIdentityAuth();
  return new ImaraClient(auth, auth.baseUrl);
}

const client = new ReauthenticatableClient(identityAuth);

/**
 * [Instantiates and] Provides an instance to perform EWA related controls
 * @returns
 */
export function ewaWebController() {
  return createWebClient({ baseUrl: env.VITE_EWA_PAYLEO_BASE_URL });
}

export function ewaClient() {
  return new EWAController(client, ewaWebController().baseUrl);
}

export function arushaClient() {
  const auth = getIdentityAuth();
  return new ArushaClient(auth, auth.baseUrl);
}

export function billingClient() {
  const auth = getIdentityAuth();
  return new BillingContext(auth, auth.baseUrl);
}

export function paymentsClient() {
  const auth = getIdentityAuth();
  return new PaymentsContext(auth, auth.baseUrl);
}

export const ewaManagerClient = () => manager(client, ewaWebController().baseUrl);
export const ewaManageOrgClient = (orgId: string) =>
  manager(client, ewaWebController().baseUrl).organization(orgId);

/**
 * Context required to call the standalone `@payleo/ewa/admin` functions
 * (e.g. `getOrgAdvances`, `recordPayment`) that take an `AdminCtx` directly
 * instead of being wrapped in a controller class.
 */
export function ewaAdminCtx(): AdminCtx {
  return { client, baseUrl: ewaWebController().baseUrl };
}
